import { Link, useLocation } from "wouter";
import { useEffect, useState } from "react";
import ThemeToggle from "@/components/ThemeToggle";

import { useOptionalAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { User, LogOut, Settings } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

const auraSvg = "/Aura_head.svg";
const auraLightSvg = "/Aura_head_light.svg";
const eirvanaLightSvg = "/Eirvana_light.svg";
const eirvanaSvg = "/Eirvana.svg";

export default function Header() {
  const [location] = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const auth = useOptionalAuth();
  const user = auth?.user ?? null;
  const isAuthenticated = auth?.isAuthenticated ?? false;
  const { toast } = useToast();
  const isAboutPage = location === "/about";
  const isSciencePage = location === "/science";
  const { theme } = useTheme();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = () => {
    if (!auth) {
      return;
    }

    auth.logout();
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
    });
    window.location.href = '/';
  };

  return (
    <header className={`sticky top-0 z-50 border-b border-[rgba(10,30,60,0.07)] transition-all duration-300 ${scrolled ? "bg-white/98 shadow-[0_12px_36px_rgba(11,31,58,0.07)] backdrop-blur-md" : "bg-white/88 backdrop-blur-md"}`}>
      <div className="max-w-[1100px] mx-auto px-6 h-[76px] grid grid-cols-3 items-center">
        <Link href="/" className="flex items-center gap-3 no-underline group">
            {theme === "night" ? (
              <img src={eirvanaLightSvg} alt="Eirvana" className="w-[42px] h-[42px] shrink-0 group-hover:scale-105 transition-transform duration-200" />
            ) : (
              <img src={eirvanaSvg} alt="Eirvana" className="w-[42px] h-[42px] shrink-0 group-hover:scale-105 transition-transform duration-200" />

            )}
          <span className="text-[18px] font-sans text-[#1C2A3A] tracking-wide">Eirvana</span>
        </Link>
        
        <div className="flex items-center justify-center gap-8">
          {isAboutPage || isSciencePage ? (
            <Link href="/" className="no-underline text-[15px] text-[#1C2A3A] font-medium font-sans tracking-[0.2px] hover:text-[#C8A24A] transition-colors duration-200">
              Home
            </Link>
          ) : null}
          {!isAboutPage && (
            <Link href="/about" className="no-underline text-[15px] text-[#1C2A3A] font-medium font-sans tracking-[0.2px] hover:text-[#C8A24A] transition-colors duration-200">
              Our Story
            </Link>
          )}
          {!isSciencePage && (
            <Link href="/science" className="no-underline text-[15px] text-[#1C2A3A] font-medium font-sans tracking-[0.2px] hover:text-[#C8A24A] transition-colors duration-200">
              The Science
            </Link>
          )}
          <a href="/#aura" className="no-underline opacity-80 hover:opacity-100 transition-opacity duration-200 flex items-center">
          {theme === "night" ? (  <img src={auraLightSvg} alt="Aura by Eirvana" className="h-[80px] w-auto" />
          ) : (  <img src={auraSvg} alt="Aura by Eirvana" className="h-[80px] w-auto" />)}</a>
        </div>

        <div className="flex items-center justify-end gap-4">
          {isAuthenticated && (
            <Link href="/chat" className="no-underline text-[14px] font-sans font-medium text-[#1C2A3A] hover:text-[#C8A24A] transition-colors duration-200">
              Chat
            </Link>
          )}
          {isAuthenticated && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-gradient-to-br from-primary to-amber-400 text-white">
                      {user.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <div className="flex flex-col space-y-1 p-2">
                  <p className="text-sm font-medium leading-none">{user.name}</p>
                  <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link href="/profile">
                    <a className="flex items-center w-full">
                      <User className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </a>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/chat">
                    <a className="flex items-center w-full">
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Chat</span>
                    </a>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link href="/register" className="no-underline text-[14px] font-sans font-medium border border-[#1C2A3A] text-[#1C2A3A] px-[18px] py-[10px] rounded-full hover:bg-[#1C2A3A] hover:text-white transition-all duration-200">
              Join waitlist
            </Link>
          )}
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}
